import { useState, useRef, useEffect } from 'react';
import { useAuthStore } from '../../store/authStore';
import { useProfile } from '../../hooks/useProfile';
import { useLanguage } from '../../contexts/LanguageContext';
import { AuthModal } from './AuthModal';
import { User, ChevronDown, Sparkles, LogOut } from 'lucide-react';
// CreditCard removed - Subscription menu temporarily disabled
import type { UserMode } from '../../hooks/useUserMode';

interface UserMenuProps {
  userMode?: UserMode;
  onUserModeChange?: (mode: UserMode) => void;
}

export function UserMenu({ userMode, onUserModeChange }: UserMenuProps) {
  const { user, isAuthenticated, logout } = useAuthStore();
  const { firstName, avatarUrl, displayName } = useProfile();
  const { language } = useLanguage();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isDropdownOpen]);

  const handleLogout = async () => {
    try {
      await logout();
      setIsDropdownOpen(false);
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  // Not authenticated - show User icon that opens auth modal directly
  if (!isAuthenticated) {
    return (
      <>
        <button
          onClick={() => setIsAuthModalOpen(true)}
          className="icon-btn"
          aria-label="Sign In"
          title="Se connecter"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '36px',
            height: '36px',
            borderRadius: '10px',
            background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.1), rgba(139, 92, 246, 0.1))',
            border: '1px solid rgba(99, 102, 241, 0.2)',
            color: '#818cf8',
            cursor: 'pointer',
            transition: 'all 0.2s',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'linear-gradient(135deg, rgba(99, 102, 241, 0.2), rgba(139, 92, 246, 0.2))';
            e.currentTarget.style.borderColor = 'rgba(99, 102, 241, 0.4)';
            e.currentTarget.style.transform = 'scale(1.05)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'linear-gradient(135deg, rgba(99, 102, 241, 0.1), rgba(139, 92, 246, 0.1))';
            e.currentTarget.style.borderColor = 'rgba(99, 102, 241, 0.2)';
            e.currentTarget.style.transform = 'scale(1)';
          }}
        >
          <User size={18} />
        </button>

        <AuthModal
          isOpen={isAuthModalOpen}
          onClose={() => setIsAuthModalOpen(false)}
        />
      </>
    );
  }

  // Get the initial letter for avatar fallback
  const avatarInitial = (firstName || displayName || user?.username || user?.email || 'U')[0].toUpperCase();

  // Authenticated - show user menu
  return (
    <div ref={dropdownRef} style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: '12px' }}>
      {/* User Avatar Button */}
      <button
        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '6px 12px',
          borderRadius: '8px',
          border: '1px solid var(--border-color)',
          background: 'var(--card-bg)',
          cursor: 'pointer',
          transition: 'all 0.2s',
        }}
        title={user?.email || 'User Menu'}
      >
        {/* Avatar Circle */}
        <div
          style={{
            width: '32px',
            height: '32px',
            borderRadius: '50%',
            background: avatarUrl
              ? `url(${avatarUrl}) center/cover no-repeat`
              : 'linear-gradient(135deg, var(--primary), #ec4899)',
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '14px',
            fontWeight: '700',
            border: '2px solid var(--border)',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          }}
        >
          {!avatarUrl && avatarInitial}
        </div>

        {/* User Info (hidden on mobile) */}
        <div
          style={{
            display: 'none',
            flexDirection: 'column',
            alignItems: 'flex-start',
          }}
          className="user-menu-info"
        >
          <span
            style={{
              fontSize: '13px',
              fontWeight: '600',
              color: 'var(--text-primary)',
            }}
          >
            {displayName || user?.username || user?.email?.split('@')[0]}
          </span>
          <span
            style={{
              fontSize: '11px',
              color: 'var(--text-secondary)',
              textTransform: 'capitalize',
            }}
          >
            {user?.subscriptionTier || 'Free'}
          </span>
        </div>

        <ChevronDown
          size={16}
          style={{
            color: 'var(--text-secondary)',
            transform: isDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s',
          }}
        />
      </button>

      {/* Dropdown Menu */}
      {isDropdownOpen && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 8px)',
            right: 0,
            minWidth: '260px',
            background: 'linear-gradient(135deg, rgba(30, 30, 40, 0.95), rgba(20, 20, 30, 0.98))',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            border: '1px solid rgba(139, 92, 246, 0.2)',
            borderRadius: '16px',
            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.4), 0 0 20px rgba(139, 92, 246, 0.1)',
            padding: '12px',
            zIndex: 1000,
          }}
        >
          {/* User Info Section with Avatar */}
          <div
            style={{
              padding: '16px',
              background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.1), rgba(236, 72, 153, 0.1))',
              borderRadius: '12px',
              marginBottom: '12px',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              border: '1px solid rgba(139, 92, 246, 0.15)',
            }}
          >
            {/* Avatar in dropdown */}
            <div
              style={{
                width: '48px',
                height: '48px',
                borderRadius: '50%',
                background: avatarUrl
                  ? `url(${avatarUrl}) center/cover no-repeat`
                  : 'linear-gradient(135deg, var(--primary), #ec4899)',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '20px',
                fontWeight: '700',
                border: '2px solid var(--border)',
                flexShrink: 0,
              }}
            >
              {!avatarUrl && avatarInitial}
            </div>

            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{
                  fontSize: '15px',
                  fontWeight: '600',
                  color: 'var(--text-primary)',
                  marginBottom: '2px',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {displayName || user?.username || 'User'}
              </div>
              <div
                style={{
                  fontSize: '12px',
                  color: 'var(--text-secondary)',
                  marginBottom: '6px',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {user?.email}
              </div>
              <div
                style={{
                  display: 'inline-block',
                  padding: '3px 8px',
                  background: 'rgba(139, 92, 246, 0.1)',
                  color: 'var(--primary)',
                  borderRadius: '6px',
                  fontSize: '10px',
                  fontWeight: '600',
                  textTransform: 'uppercase',
                }}
              >
                {user?.subscriptionTier || 'Free'}
              </div>
            </div>
          </div>

          {/* Menu Items */}
          <button
            onClick={() => {
              window.dispatchEvent(new CustomEvent('navigate-to-settings'));
              setIsDropdownOpen(false);
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              width: '100%',
              padding: '12px 14px',
              borderRadius: '10px',
              border: 'none',
              background: 'transparent',
              color: 'rgba(255, 255, 255, 0.9)',
              fontSize: '14px',
              fontWeight: 500,
              cursor: 'pointer',
              transition: 'all 0.2s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(139, 92, 246, 0.15)';
              e.currentTarget.style.transform = 'translateX(4px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent';
              e.currentTarget.style.transform = 'translateX(0)';
            }}
          >
            <User size={18} style={{ color: '#a78bfa' }} />
            {language === 'fr' ? 'Mon Profil' : 'My Profile'}
          </button>

          {/* Subscription menu - temporarily disabled
          <button
            onClick={() => {
              window.dispatchEvent(new CustomEvent('navigate-to-pricing'));
              setIsDropdownOpen(false);
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              width: '100%',
              padding: '12px 14px',
              borderRadius: '10px',
              border: 'none',
              background: 'transparent',
              color: 'rgba(255, 255, 255, 0.9)',
              fontSize: '14px',
              fontWeight: 500,
              cursor: 'pointer',
              transition: 'all 0.2s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(139, 92, 246, 0.15)';
              e.currentTarget.style.transform = 'translateX(4px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent';
              e.currentTarget.style.transform = 'translateX(0)';
            }}
          >
            <CreditCard size={18} style={{ color: '#60a5fa' }} />
            {language === 'fr' ? 'Abonnement' : 'Subscription'}
          </button>
          */}

          {/* Simple/Expert Mode Toggle */}
          {onUserModeChange && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                width: '100%',
                padding: '12px 14px',
                borderRadius: '10px',
              }}
            >
              <Sparkles size={18} style={{ color: '#fbbf24' }} />
              <span style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '14px', fontWeight: 500, flex: 1 }}>
                Mode
              </span>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  padding: '3px 4px',
                  background: 'rgba(0, 0, 0, 0.3)',
                  borderRadius: '8px',
                  fontSize: '11px',
                  fontWeight: 500,
                }}
              >
                <button
                  onClick={() => onUserModeChange('simple')}
                  style={{
                    padding: '5px 10px',
                    borderRadius: '6px',
                    border: 'none',
                    background: userMode === 'simple' ? 'var(--primary)' : 'transparent',
                    color: userMode === 'simple' ? 'white' : 'rgba(255,255,255,0.5)',
                    cursor: 'pointer',
                    fontWeight: userMode === 'simple' ? 600 : 500,
                    transition: 'all 0.2s ease',
                    fontSize: '11px',
                  }}
                >
                  Simple
                </button>
                <button
                  onClick={() => onUserModeChange('expert')}
                  style={{
                    padding: '5px 10px',
                    borderRadius: '6px',
                    border: 'none',
                    background: userMode === 'expert' ? 'var(--primary)' : 'transparent',
                    color: userMode === 'expert' ? 'white' : 'rgba(255,255,255,0.5)',
                    cursor: 'pointer',
                    fontWeight: userMode === 'expert' ? 600 : 500,
                    transition: 'all 0.2s ease',
                    fontSize: '11px',
                  }}
                >
                  Expert
                </button>
              </div>
            </div>
          )}

          <div
            style={{
              height: '1px',
              background: 'linear-gradient(90deg, transparent, rgba(139, 92, 246, 0.3), transparent)',
              margin: '10px 0',
            }}
          />

          <button
            onClick={handleLogout}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              width: '100%',
              padding: '12px 14px',
              borderRadius: '10px',
              border: 'none',
              background: 'transparent',
              color: '#f87171',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(239, 68, 68, 0.15)';
              e.currentTarget.style.transform = 'translateX(4px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent';
              e.currentTarget.style.transform = 'translateX(0)';
            }}
          >
            <LogOut size={18} />
            {language === 'fr' ? 'Déconnexion' : 'Sign Out'}
          </button>
        </div>
      )}

      {/* CSS for responsive visibility */}
      <style>
        {`
          @media (min-width: 640px) {
            .user-menu-info {
              display: flex !important;
            }
          }
        `}
      </style>
    </div>
  );
}
